import type {
	BreakpointRange,
	ReactionsInBpRange,
	StateReactionData,
	EffectsReactionData,
	VariantDataItem,
	ReactionsInBpRangeWithTriggerData,
	ResolvedVariantRelation,
	Reactions,
	MobileVariant,
	TriggerVariant,
	AnimationsBehavior,
} from '@wix/thunderbolt-becky-types'
import _ from 'lodash'

export type ReactionWithCondition = {
	triggerBpId: string
	reactions: Array<StateReactionData | EffectsReactionData>
	triggerData?: VariantDataItem
}

export type BreakpointsData = { breakpointsRanges: { [id: string]: BreakpointRange }; breakpointsOrder: Array<string> }

const minusInfinity = -1
const plusInfinity = 1000000000

const orMinusInfinity = (value?: number): number => value || minusInfinity
const orPlusInfinity = (value?: number): number => value || plusInfinity

const subtractBreakpointRange = (minuend: BreakpointRange, subtrahend: BreakpointRange): Array<BreakpointRange> => {
	const intersectionEmpty =
		orMinusInfinity(minuend.min) > orPlusInfinity(subtrahend.max) ||
		orPlusInfinity(minuend.max) < orMinusInfinity(subtrahend.min)
	if (intersectionEmpty) {
		return [minuend]
	}

	const intersectionEqualsMinuend =
		orMinusInfinity(subtrahend.min) <= orMinusInfinity(minuend.min) &&
		orPlusInfinity(subtrahend.max) >= orPlusInfinity(minuend.max)
	if (intersectionEqualsMinuend) {
		return []
	}

	const shouldSplit =
		orMinusInfinity(subtrahend.min) > orMinusInfinity(minuend.min) &&
		orPlusInfinity(subtrahend.max) < orPlusInfinity(minuend.max)

	const shouldTrimLeft = !shouldSplit && orPlusInfinity(subtrahend.max) < orPlusInfinity(minuend.max)

	if (shouldSplit) {
		return [
			{ ...minuend, max: subtrahend.min! - 1 },
			{ ...minuend, min: subtrahend.max! + 1 },
		]
	}

	if (shouldTrimLeft) {
		return [{ ...minuend, min: subtrahend.max! + 1 }]
	}

	// trim right
	return [{ ...minuend, max: subtrahend.min! - 1 }]
}

const subtractManyBreakpointRanges = (
	minuend: BreakpointRange,
	subtrahends: Array<BreakpointRange>
): Array<BreakpointRange> => {
	let result = [minuend]

	subtrahends.forEach((nextSubtrahend) => {
		let nextResult: Array<BreakpointRange> = []
		result.forEach((nextMinuend) => {
			nextResult = [...nextResult, ...subtractBreakpointRange(nextMinuend, nextSubtrahend)]
		})
		result = nextResult
	})

	return result
}

const resolveOverlappingRanges = (cascadingRanges: Array<BreakpointRange>): Array<BreakpointRange> => {
	const resolvedRanges: Array<BreakpointRange> = []

	for (let i = cascadingRanges.length - 1; i >= 0; i--) {
		const range = cascadingRanges[i]
		resolvedRanges.push(...subtractManyBreakpointRanges(range, resolvedRanges))
	}

	return resolvedRanges
}

const defaultBreakpointRange: BreakpointRange = { min: undefined, max: undefined, id: '', type: 'BreakpointRange' }

const getReactionsBreakpointsRanges = (
	reactionsInBp: Array<ReactionWithCondition | { triggerBpId: string }>,
	{ breakpointsRanges, breakpointsOrder }: BreakpointsData
): Array<BreakpointRange> => {
	const bpIdsInUse = reactionsInBp.reduce<{ [bpId: string]: true }>((acc, { triggerBpId }) => {
		acc[triggerBpId] = true
		return acc
	}, {})

	const reactionsBPsIds = ['', ...breakpointsOrder].filter((bpId) => bpIdsInUse[bpId])
	const breakpointsRangesWithDefault: { [bpId: string]: BreakpointRange } = {
		...breakpointsRanges,
		'': defaultBreakpointRange,
	}

	return reactionsBPsIds.map((bpId) => breakpointsRangesWithDefault[bpId])
}

const resolveBpRanges = (
	scrubEffectToTrigger: Array<ReactionWithCondition | { triggerBpId: string }>,
	breakpointsData: BreakpointsData
) => resolveOverlappingRanges(getReactionsBreakpointsRanges(scrubEffectToTrigger, breakpointsData))

const assignKeyToObject = (key: string, object: Record<string, any>): Record<string, any> => {
	return { [key]: object }
}

const toNonCascadingRanges = (
	reactionsInBp: Array<ReactionWithCondition>,
	breakpointsData: BreakpointsData
): Array<ReactionsInBpRange | ReactionsInBpRangeWithTriggerData> => {
	const resolvedRanges = resolveOverlappingRanges(getReactionsBreakpointsRanges(reactionsInBp, breakpointsData))

	const result: Array<ReactionsInBpRange> = _.flatten(
		resolvedRanges.map<Array<ReactionsInBpRange>>((range) => {
			const reactions: Array<ReactionsInBpRange> = []
			reactionsInBp.forEach((reaction) => {
				if (reaction.triggerBpId === range.id) {
					reactions.push({
						...(reaction.triggerData && { triggerData: reaction.triggerData }),
						triggerBpRange: {
							min: range.min,
							max: range.max,
						},
						reactions: reaction?.reactions || [],
					})
				}
			})
			return reactions
		})
	)

	result.sort((a, b) => orMinusInfinity(a.triggerBpRange.min) - orMinusInfinity(b.triggerBpRange.min))
	return result
}

const getKeyReactionsByIdAccumulative = (
	variantKeyToReactionsIsd: Array<{ id: string; reactionsIds: Array<string> }>
) =>
	variantKeyToReactionsIsd.reduce<{ [variantId: string]: Array<string> }>(
		(acc, current) => ({
			...acc,
			[current.id]: [...(acc[current.id] || []), ...current.reactionsIds],
		}),
		{}
	)

const getUniqueObjects = (objectsToCompare: Array<Record<string, any>>) =>
	_.uniqWith(objectsToCompare, (objectA, objectB) => _.isEqual(objectA, objectB))

const transformRelationsArrayToMap = (
	variantRelations: Array<ResolvedVariantRelation<Reactions>>,
	triggersMap: { [variantId: string]: TriggerVariant },
	sideEffect?: (triggerType: TriggerVariant['trigger']) => void
) => {
	return variantRelations.reduce((acc, item) => {
		const triggerId = item.variants.find((variantId) => variantId.replace('#', '') in triggersMap)

		if (triggerId) {
			const _triggerType = triggersMap[triggerId.replace('#', '')].trigger
			acc[_triggerType] = item
			sideEffect?.(_triggerType)
		}

		return acc
	}, {} as Record<TriggerVariant['trigger'], ResolvedVariantRelation<Reactions>>)
}

const getAdaptiveCascadedReactions = (
	variantRelations: Array<ResolvedVariantRelation<Reactions>>,
	mobileVariantMap: { [variantId: string]: MobileVariant },
	triggersMap: { [variantId: string]: TriggerVariant },
	isMobile: boolean
): Array<ResolvedVariantRelation<Reactions>> => {
	const desktopItems = variantRelations.filter(
		(item) => !item.variants.some((variantId) => variantId.replace('#', '') in mobileVariantMap)
	)

	if (!isMobile) {
		return desktopItems
	}

	const allTriggerTypes: Set<TriggerVariant['trigger']> = new Set()
	const addTriggerTypeToSet = (triggerType: TriggerVariant['trigger']) => {
		allTriggerTypes.add(triggerType)
	}

	const mobileItems = variantRelations.filter((item) =>
		item.variants.some((variantId) => variantId.replace('#', '') in mobileVariantMap)
	)

	const desktopItemByTriggerTypeMap = transformRelationsArrayToMap(desktopItems, triggersMap, addTriggerTypeToSet)
	const mobileItemByTriggerTypeMap = transformRelationsArrayToMap(mobileItems, triggersMap, addTriggerTypeToSet)

	return Array.from(allTriggerTypes).map(
		(triggerType: TriggerVariant['trigger']) =>
			mobileItemByTriggerTypeMap[triggerType] || desktopItemByTriggerTypeMap[triggerType]
	)
}

const getDynamicAncestorToChildrenMap = (compList: Array<{ parent: string; child: string; pageId: string }>) => {
	const result: Record<string, { pageId: string; children: Array<string> }> = {}

	compList.forEach((comp) => {
		if (!result[comp.parent]) {
			result[comp.parent] = {
				// @ts-expect-error
				children: new Set(),
				pageId: comp.pageId,
			}
		}

		// @ts-expect-error - use a Set to avoid duplicates
		result[comp.parent].children.add(comp.child)
	})

	// cast to Array
	Object.values(result).forEach((data) => {
		data.children = Array.from(data.children)
	})

	return result
}

const transformBgScrubToReactions = (compIdToBgScrubs: { [compId: string]: Array<AnimationsBehavior> }) => {
	return Object.entries(compIdToBgScrubs).flatMap(([compId, bgScrubs]) => {
		return bgScrubs.map((scrub) => {
			const reactionId = `reaction-${scrub.behaviorId}`

			return {
				triggerComponentId: compId,
				targetCompId: compId,
				triggerType: 'view-progress',
				triggerBpId: '',
				reactions: [
					{
						type: 'Scrub',
						id: reactionId,
						reactionData: {
							effect: scrub.behaviorId as string,
							id: reactionId,
						},
					},
				],
				triggerBpRange: {
					min: undefined,
					max: undefined,
				},
				triggerData: {
					trigger: 'view-progress',
					componentId: compId,
				},
			}
		})
	})
}

export {
	toNonCascadingRanges,
	subtractBreakpointRange,
	getUniqueObjects,
	getKeyReactionsByIdAccumulative,
	resolveBpRanges,
	getAdaptiveCascadedReactions,
	getDynamicAncestorToChildrenMap,
	transformBgScrubToReactions,
	assignKeyToObject,
}
